/**
 * OrderSummaryCard Component
 *
 * Displays a summary of the order including location, container selection, and schedule.
 * Used in the details step to confirm order information before proceeding to checkout.
 */

import { MapPin, Package, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import type { LocationData, ContainerSelection, ScheduleSelection } from '../../types/order-builder';

export interface OrderSummaryCardProps {
  location: LocationData;
  container: ContainerSelection;
  schedule: ScheduleSelection;
}

/**
 * Format date for display
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format container size for display
 */
function formatContainerSize(size: string): string {
  // Convert size like "20_YARD" to "20 Yard"
  return size.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Format material type for display
 */
function formatMaterialType(materialType: string): string {
  // Convert material type like "MIXED_CD" to "Mixed C&D"
  const formatted = materialType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  // Special case for C&D
  return formatted.replace(/Cd\b/, 'C&D');
}

/**
 * OrderSummaryCard Component
 */
export function OrderSummaryCard({
  location,
  container,
  schedule,
}: OrderSummaryCardProps) {
  return (
    <Card className="bg-primary-50 border-primary-200">
      <CardHeader>
        <CardTitle className="text-lg">Order Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Location */}
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <MapPin className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Delivery Location</p>
            <p className="text-sm text-gray-700 mt-1">
              {location.address}
              <br />
              {location.city}, {location.state} {location.zipCode}
            </p>
          </div>
        </div>

        {/* Container */}
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Package className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Container Selection</p>
            <p className="text-sm text-gray-700 mt-1">
              {formatContainerSize(container.size)}
              <br />
              Material: {formatMaterialType(container.materialType)}
            </p>
          </div>
        </div>

        {/* Schedule */}
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Calendar className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Delivery Schedule</p>
            <p className="text-sm text-gray-700 mt-1">
              {formatDate(schedule.deliveryDate)}
              {schedule.isASAP && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  ASAP
                </span>
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
